import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  useLogout,
  usePermissions,
  useRole,
  useRolePermissions,
} from "../../_api/useAuth";
import MyLoader from "../loader/MyLoader.tsx";
import HasRole from "./HasRole.tsx";

type MenusProps = {
  isDarkMode: boolean;
};

function SidebarNavSkeleton({ isDarkMode }: { isDarkMode: boolean }) {
  return (
    <nav className="mt-4 px-3 space-y-1.5 animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl p-3">
          <div className="h-5 w-5 flex-shrink-0 rounded bg-gray-300 dark:bg-gray-700" />
          <div className="hidden md:block h-4 w-32 rounded bg-gray-300 dark:bg-gray-700" />
        </div>
      ))}
    </nav>
  );
}

const Menus = ({ isDarkMode }: MenusProps) => {
  const [activeMenu, setActiveMenu] = useState<string>("dashboard");
  const location = useLocation();
  let fullPath = location.pathname.split("/");
  let currentPath = fullPath[1];

  const logout = useLogout();

  //get role and permission loading state
  const { isPending: isRoleLoading } = useRole();
  const { isPending: isRolePermissionLoading } = useRolePermissions();
  const { isPending: isPermissionLoading } = usePermissions();

  // Style commun d'un item de navigation : icône alignée dans une colonne fixe,
  // état actif avec fond plein + ombre légère, sinon simple survol.
  const navItemClass = (isActive: boolean) =>
    `group p-3 mx-3 flex items-center justify-center md:justify-start gap-3 rounded-xl mb-1 cursor-pointer transition-colors ${
      isActive
        ? "bg-blue-500 text-white shadow-sm shadow-blue-500/30"
        : isDarkMode
          ? "text-gray-300 hover:bg-gray-700/70 hover:text-white"
          : "text-gray-600 hover:bg-gray-200/70 hover:text-gray-900"
    }`;

  // Les icônes Font Awesome héritent de la couleur du texte du lien parent
  // (currentColor) : pas besoin de logique de couleur séparée ici.
  const navIconClass = "w-5 flex-shrink-0 text-center";

  return (
    <div
      className={`sm:w-20 md:w-60 min-h-screen fixed flex flex-col ${
        isDarkMode ? "bg-[#161B22]" : "bg-gray-100"
      } border-r ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
    >
      {/* <div className="relative top-10"> */}
      <MyLoader
        isLoading={logout.isPending}
        message="  Déconnexion en cours..."
      />
      {/* </div> */}

      {/* Logo */}
      <div
        className={`p-4 border-solid border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <div className="flex items-center justify-center md:justify-start gap-2.5">
          <img
            src="/images/chiksoft_blue.png"
            alt="logo"
            width={36}
            className="flex-shrink-0 rounded-md"
          />
          <h1 className="hidden md:block text-2xl font-mono font-bold tracking-tight text-[#00a0eb]">
            ChikSoft
          </h1>
        </div>
      </div>

      {/* Navigation */}
      {isRoleLoading || isRolePermissionLoading || isPermissionLoading ? (
        <SidebarNavSkeleton isDarkMode={isDarkMode} />
      ) : (
        <nav className="mt-4 flex-1 overflow-y-auto">
          <Link
            to="/"
            onClick={() => setActiveMenu("dashboard")}
            className={navItemClass(currentPath === "")}
          >
            <i className={`fas fa-th-large ${navIconClass}`}></i>
            <span className="hidden md:block text-sm font-medium">
              Tableau de bord
            </span>
          </Link>

          <Link
            to="/eleves"
            onClick={() => setActiveMenu("students")}
            className={navItemClass(currentPath === "eleves")}
          >
            <i className={`fas fa-user-graduate ${navIconClass}`}></i>
            <span className="hidden md:block text-sm font-medium">Élèves</span>
          </Link>

          <Link
            to="/attendance"
            onClick={() => setActiveMenu("attendance")}
            className={navItemClass(currentPath === "attendance")}
          >
            <i className={`fas fa-clipboard-check ${navIconClass}`}></i>
            <span className="hidden md:block text-sm font-medium">
              Présences
            </span>
          </Link>

          <HasRole
            allowedRoles={[
              "SuperAdministrateur",
              "Administrateur",
              "Comptable",
            ]}
          >
            <Link
              to={"/paiement"}
              onClick={() => setActiveMenu("paiement")}
              className={navItemClass(currentPath === "paiement")}
            >
              <i className={`fas fa-credit-card ${navIconClass}`}></i>
              <span className="hidden md:block text-sm font-medium">
                Paiements
              </span>
            </Link>
          </HasRole>

          <HasRole
            allowedRoles={[
              "SuperAdministrateur",
              "Administrateur",
              "Comptable",
            ]}
          >
            <Link
              to={"/message"}
              onClick={() => setActiveMenu("message")}
              className={navItemClass(currentPath === "message")}
            >
              <i className={`fas fa-comments ${navIconClass}`}></i>
              <span className="hidden md:block text-sm font-medium">
                Communications
              </span>
            </Link>
          </HasRole>

          <HasRole
            allowedRoles={[
              "SuperAdministrateur",
              "Administrateur",
              "Comptable",
            ]}
          >
            <Link
              to={"/parametre"}
              onClick={() => setActiveMenu("parametre")}
              className={navItemClass(currentPath === "parametre")}
            >
              <i className={`fas fa-cog ${navIconClass}`}></i>
              <span className="hidden md:block text-sm font-medium">
                Paramètres
              </span>
            </Link>
          </HasRole>
        </nav>
      )}

      {/* Déconnexion */}
      <div
        className={`px-3 py-4 border-t ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <button
          onClick={() => logout.mutate()}
          className={`p-3 w-full flex items-center justify-center md:justify-start gap-3 rounded-xl text-red-500 cursor-pointer transition-colors ${
            isDarkMode ? "hover:bg-red-500/10" : "hover:bg-red-50"
          } !rounded-button whitespace-nowrap`}
        >
          <i className="fas fa-sign-out-alt w-5 flex-shrink-0 text-center"></i>
          <span className="hidden md:block text-sm font-medium">
            Déconnexion
          </span>
        </button>
      </div>
    </div>
  );
};

export default Menus;
